/* Generic ticketed-event booking success page. Routes by metadata.event_key returned from API. */
import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Sparkles, Ticket, Calendar, Clock, Users, ArrowRight, AlertCircle, Loader2, Download, MapPin, Crown } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 6;
const POLL_INTERVAL = 2500;

// Match BookEvent themes
const THEMES = {
 "aus-day-2026-day": { accent: "#E8B53A", hot: "#FFE08A", dark3: "#0A1417", parentSlug: "australia-day-cruises-sydney-harbour", title: "Australia Day Cruise", subtitle: "Day session" },"aus-day-2026-night":{ accent: "#E8B53A", hot: "#FFCC70", dark3: "#0A0212", parentSlug: "australia-day-cruises-sydney-harbour", title: "Australia Day Cruise", subtitle: "Night & Fireworks" },"boxing-day-2026": { accent: "#DC2626", hot: "#F87171", dark3: "#08111F", parentSlug: "boxing-day-cruises-sydney", title: "Boxing Day Cruise", subtitle: "Sydney to Hobart" },
};

export default function EventBookingSuccess({ slugDefault }) {
 const [params] = useSearchParams();
 const sessionId = params.get("session_id");
 const [state, setState] = useState({ status: "checking", data: null, error: "" });
 const polledRef = useRef(0);

 useEffect(() => { document.title = "Booking Confirmed | Magic Cruises"; }, []);

 useEffect(() => {
  if (!sessionId) { setState({ status: "error", data: null, error: "No payment session in URL" }); return; }
  let cancelled = false;
  const poll = async () => {
   try {
    try { await axios.post(`${API}/checkout/finalize/${sessionId}`); } catch (_) { /* already finalized */ }
    const r = await axios.get(`${API}/checkout/status/${sessionId}`);
    if (cancelled) return;
    const ps = r.data.payment_status;
    if (ps === "paid") { setState({ status: "paid", data: r.data, error: "" }); return; }
    if (r.data.status === "expired") { setState({ status: "expired", data: r.data, error: "Session expired" }); return; }
    polledRef.current += 1;
    if (polledRef.current >= MAX_POLLS) { setState({ status: "timeout", data: r.data, error: "Still processing, check your email" }); return; }
    setTimeout(poll, POLL_INTERVAL);
   } catch (e) {
    if (cancelled) return;
    setState({ status: "error", data: null, error: e?.response?.data?.detail || "Unable to verify payment" });
   }
  };
  poll();
  return () => { cancelled = true; };
 }, [sessionId]);

 const meta = state.data?.metadata || {};
 const eventKey = meta.event_key;
 const theme = THEMES[eventKey] || { accent: "#E8B53A", hot: "#FFE08A", dark3: "#0A1417", parentSlug: slugDefault || "australia-day-cruises-sydney-harbour", title: "Cruise", subtitle: "" };
 const order = state.data?.order || {};
 const amount = state.data?.amount_total ? (state.data.amount_total / 100) : (order.amount || 0);

 return (
  <main className="text-white min-h-screen relative overflow-hidden" style={{ backgroundColor: theme.dark3 }} data-testid="event-booking-success">
   <SEOHead title="Booking Confirmation | Magic Cruises" description={`Your ${theme.title} booking is confirmed.`} />
   <div className="absolute inset-0 pointer-events-none">
    <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl animate-pulse" style={{ backgroundColor: `${theme.accent}22` }} />
    <div className="absolute bottom-0 right-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1.5s", backgroundColor: `${theme.hot}1A` }} />
   </div>
   <Starfield density={60} color={theme.hot} glow={`${theme.hot}99`} fixed={true} intensity={0.55} />

   <div className="relative pt-32 pb-32 px-5 lg:px-10 mx-auto max-w-3xl">
    {state.status === "checking" && (
     <div className="text-center" data-testid="event-booking-checking">
      <Loader2 className="w-12 h-12 mx-auto mb-6 animate-spin" style={{ color: theme.accent }} />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Confirming your payment…</h1>
      <p className="text-white/65 text-lg">Hold tight, this usually takes a couple of seconds.</p>
     </div>
    )}

    {state.status === "paid" && (
     <div data-testid="event-booking-paid">
      <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center shadow-2xl mb-7" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)`, boxShadow: `0 20px 50px -10px ${theme.hot}99` }}>
       <Check className="w-10 h-10 text-[#0A0A0A]" strokeWidth={3} />
      </div>
      <div className="text-center mb-12">
       <div className="text-[11px] uppercase tracking-[0.32em] font-bold mb-3" style={{ color: theme.accent }}>Payment Confirmed · {theme.subtitle}</div>
       <h1 className="font-black tracking-tight leading-[0.98] mb-5" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", fontFamily: "'Playfair Display', serif" }}>
        See you on <em className="italic font-light bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)` }}>the harbour.</em>
       </h1>
       <p className="text-white/82 text-lg leading-[1.7] max-w-xl mx-auto">
        Your {theme.title} tickets are confirmed. We've sent your boarding pass to <strong className="text-white">{meta.customer_email}</strong>.
       </p>
      </div>

      <div className="rounded-3xl ring-1 p-7 lg:p-8 mb-8 backdrop-blur-md" style={{ background: `linear-gradient(135deg, ${theme.accent}1A, rgba(0,0,0,0.5) 60%)`, borderColor: `${theme.accent}33`, boxShadow: `0 30px 80px -20px ${theme.hot}55` }}>
       <div className="text-[11px] uppercase tracking-[0.32em] font-bold mb-5 flex items-center gap-2" style={{ color: theme.accent }}><Ticket className="w-3.5 h-3.5" /> Your Ticket</div>
       <dl className="space-y-4">
        <SummaryRow Icon={Calendar} label="Date" value={meta.date ? new Date(meta.date).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""} accent={theme.accent} />
        <SummaryRow Icon={Clock} label="Boarding" value={meta.boarding || ""} accent={theme.accent} />
        <SummaryRow Icon={Clock} label="Returns" value={meta.return ? `~${meta.return}` : ""} accent={theme.accent} />
        <SummaryRow Icon={MapPin} label="Wharf" value={meta.wharf || ""} accent={theme.accent} />
        <SummaryRow Icon={Users} label="Tickets" value={`${meta.adult_qty || 0} adult · ${meta.child_qty || 0} child`} accent={theme.accent} />
        <SummaryRow Icon={Crown} label="Event" value={meta.event_name || theme.title} accent={theme.accent} />
       </dl>
       <div className="border-t border-white/15 mt-6 pt-5 flex items-baseline justify-between">
        <span className="text-white/65 uppercase tracking-widest text-[10px] font-bold">Total paid</span>
        <span className="text-3xl font-black bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)` }}>${amount.toLocaleString()} AUD</span>
       </div>
       <div className="text-white/45 text-[10px] uppercase tracking-widest font-bold text-right">Reference · {state.data?.session_id?.slice(-8) || ""}</div>
      </div>

      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 mb-8 flex items-start gap-4">
       <Download className="w-5 h-5 shrink-0 mt-0.5" style={{ color: theme.accent }} />
       <div className="flex-1">
        <div className="text-white font-bold mb-1">Your boarding pass is ready</div>
        <div className="text-white/82 text-sm leading-[1.7]">Premium boarding pass with QR code. Show it on your phone at the gangway or print it.</div>
       </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
       <a href={`${API}/tickets/${state.data?.session_id}/pdf`} target="_blank" rel="noopener noreferrer" className="px-7 py-4 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] transition-all hover:scale-[1.02] inline-flex items-center gap-2" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)`, boxShadow: `0 15px 40px -10px ${theme.hot}99` }} data-testid="event-success-download-pdf">
        <Download className="w-4 h-4" /> Download Boarding Pass
       </a>
       <Link to={`/cruises/${theme.parentSlug}`} className="px-7 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-[0.22em] border border-white/20 transition-colors" data-testid="event-success-back">
        Back to {theme.title}
       </Link>
      </div>
     </div>
    )}

    {state.status === "expired" && (
     <div className="text-center" data-testid="event-booking-expired">
      <AlertCircle className="w-14 h-14 mx-auto mb-5" style={{ color: theme.accent }} />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Session expired</h1>
      <p className="text-white/65 text-base mb-7">No charge was made. Please try booking again.</p>
      <Link to={`/cruises/${theme.parentSlug}/book?event=${eventKey || ""}`} className="inline-flex px-7 py-4 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em]" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)` }}>Try again</Link>
     </div>
    )}

    {state.status === "timeout" && (
     <div className="text-center" data-testid="event-booking-timeout">
      <Loader2 className="w-12 h-12 mx-auto mb-5" style={{ color: theme.accent }} />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Still processing</h1>
      <p className="text-white/65 text-base mb-2 max-w-xl mx-auto leading-[1.7]">Your bank is taking longer than usual to confirm. Your email at <strong className="text-white">{meta.customer_email || "the address you provided"}</strong> will receive confirmation as soon as it clears. You can safely close this page.</p>
     </div>
    )}

    {state.status === "error" && (
     <div className="text-center" data-testid="event-booking-error">
      <AlertCircle className="w-14 h-14 text-red-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Something went wrong</h1>
      <p className="text-white/65 text-base mb-7">{state.error}</p>
      <Link to={`/cruises/${theme.parentSlug}`} className="inline-flex px-7 py-4 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em]" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)` }}>Back</Link>
     </div>
    )}
   </div>
  </main>
 );
}

function SummaryRow({ Icon, label, value, accent }) {
 return (
  <div className="flex items-start gap-3">
   <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
   <div className="flex-1 flex justify-between gap-3 flex-wrap">
    <span className="text-white/55 uppercase tracking-widest text-[10px] font-bold">{label}</span>
    <span className="text-white font-semibold text-right max-w-md">{value}</span>
   </div>
  </div>
 );
}
